import { useMemo, useState } from "react";
import { deepArticles } from "@/pages/home/data";

const FILTERS = ["全部", "综艺幕后", "幕后真相", "品牌瓜", "直播热点", "吃瓜指南", "舆论观察", "细节深挖"];

export default function DeepPicks() {
  const [active, setActive] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    let list = active === "全部" ? deepArticles : deepArticles.filter((a) => a.category === active);
    const kw = keyword.trim();
    if (kw) {
      const lower = kw.toLowerCase();
      list = list.filter(
        (a) => a.title.toLowerCase().includes(lower) || a.excerpt.toLowerCase().includes(lower) || a.category.includes(kw)
      );
    }
    return list;
  }, [active, keyword]);

  return (
    <section id="picks" className="scroll-anchor bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <span className="h-px w-8 bg-primary-500"></span>
              深扒精选
            </div>
            <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              挑一瓜，往下挖
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              按栏目浏览深扒内容，或用关键词搜索你关心的主题，编辑部已把每篇内容的线索梳理得整整齐齐。
            </p>
          </div>

          {/* 搜索 */}
          <div className="w-full md:w-72">
            <label htmlFor="pick-search" className="sr-only">
              搜索深扒内容
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-200/60">
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
              <input
                id="pick-search"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索栏目、标题或关键词…"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  aria-label="清空搜索"
                  className="flex items-center justify-center text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-circle-fill" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 筛选 chips */}
        <div className="mt-7 flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => {
            const selected = active === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setActive(f)}
                className={
                  "inline-flex items-center whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors " +
                  (selected
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-50 text-foreground-700 hover:bg-background-200")
                }
              >
                {f}
              </button>
            );
          })}
        </div>

        {/* 卡片网格 */}
        {filtered.length > 0 ? (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a) => (
              <article
                key={a.id}
                className="group flex flex-col overflow-hidden rounded-lg bg-background-50 transition-colors hover:bg-background-50"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.alt}
                    className="aspect-[3/2] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-sm bg-primary-500 px-2 py-0.5 text-xs font-bold text-background-50">
                    {a.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                    {a.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{a.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-3-line" aria-hidden="true"></i>
                        {a.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        {a.readTime}
                      </span>
                    </div>
                    <span className="flex items-center gap-1 text-foreground-600">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {a.views}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-xl bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-2-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-medium text-foreground-800">没有搜到相关深扒内容</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或者看看其它栏目</p>
            <button
              type="button"
              onClick={() => {
                setActive("全部");
                setKeyword("");
              }}
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              清空筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}